import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./SignIn.css";

import handLeft from "../../assets/left-hand.png";
import handRight from "../../assets/right-hand.png";
import { useAuthBootstrap } from "../../context/AuthBootstrapContext";

const API_BASE = "https://api-2026.fikacreatives.co";

function getReadableError(errorParam) {
  switch (errorParam) {
    case "invalid_state":
      return "Your sign-in session expired. Please try again.";
    case "missing_code":
      return "Google did not return a sign-in code. Please try again.";
    case "missing_id_token":
      return "Google sign-in could not be completed. Please try again.";
    case "google_account_not_verified":
      return "Please use a Google account with a verified email.";
    case "no_account_found":
      return "We couldn’t find an existing Fika account for that Google email.";
    case "google_signin_failed":
      return "Google sign-in failed. Please try again.";
    case "application_incomplete":
      return "Your application isn’t complete yet. You’ll receive an email once you have access to sign in.";
    default:
      return errorParam ? `Sign-in error: ${errorParam}` : "";
  }
}

function getSafeNextPath(location) {
  const statePath = location.state?.from?.pathname;
  const queryPath = new URLSearchParams(location.search).get("next");
  const candidate = String(statePath || queryPath || "/fikas").trim();

  if (!candidate.startsWith("/")) return "/fikas";
  if (candidate.startsWith("/api/")) return "/fikas";
  if (candidate === "/sign-in") return "/fikas";

  return candidate;
}

export default function SignIn() {
  const location = useLocation();
  const navigate = useNavigate();

  const { loading, authed, person, error, onboardingCompleted } =
    useAuthBootstrap();

  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState("");

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const errorParam = searchParams.get("error");
  const errorMessage = getReadableError(errorParam) || error;

  useEffect(() => {
    if (loading) return;
    if (!authed && !person) return;

    const nextPath = getSafeNextPath(location);

    if (!onboardingCompleted) {
      navigate("/fikas", { replace: true });
      return;
    }

    navigate(nextPath, { replace: true });
  }, [loading, authed, person, onboardingCompleted, navigate, location]);

  function onGoogleSignIn() {
    const nextPath = getSafeNextPath(location);
    const next = encodeURIComponent(nextPath);
    window.location.href = `${API_BASE}/api/auth/google/start?next=${next}`;
  }

  // ✅ THIS is what you were missing
  async function handleEmailLogin(e) {
    e.preventDefault();
    setEmailLoading(true);
    setEmailError("");

    try {
      const res = await fetch(`${API_BASE}/api/auth/email/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to send login link");
      }

      setEmailSent(true);
    } catch (err) {
      setEmailError(err.message || "Something went wrong");
    } finally {
      setEmailLoading(false);
    }
  }

  return (
    <div className="signInPage">
      <img
        src={handLeft}
        style={{ width: "200px" }}
        alt="hand-left"
        className="signInHand signInHand--left"
      />
      <img
        src={handRight}
        style={{ width: "200px" }}
        alt="hand-right"
        className="signInHand signInHand--right"
      />

      <main className="signInCard" aria-label="Sign in">
        <div style={{ margin: "auto", width: "100%", textAlign: "center" }}>
          <img
            src="https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/sign/landing%20page/blue%20fika%20logo.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZGI0MGU3NC04Nzk2LTRhZmItOTViNy0wNDQ5YzAyNWMzZjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsYW5kaW5nIHBhZ2UvYmx1ZSBmaWthIGxvZ28ucG5nIiwiaWF0IjoxNzc2ODM1MjA0LCJleHAiOjE4MDgzNzEyMDR9.4xMRW0XoSRLWW0gAVE1FhNYfJuDanwsWGnDGxPucKYg"
            style={{ width: "100px", marginBottom: "32px" }}
            alt="fika-logo"
          />
        </div>

        <h1 className="signInTitle">Welcome back</h1>

        <p
          style={{
            textAlign: "left",
            marginTop: "-4px",
            marginBottom: "20px",
            color: "#6b6b6b",
          }}
        >
          Sign in to Fika to connect over coffee.
        </p>

        {errorMessage && (
          <p style={{ color: "#8D2D2D", fontSize: "14px" }}>
            {errorMessage}
          </p>
        )}

        <button className="signInBtn" onClick={onGoogleSignIn}>
          Continue with Google
        </button>

        {/* ✅ Email Login */}
        {!emailSent ? (
          <form onSubmit={handleEmailLogin} className="signInForm" style={{ marginTop: "16px" }}>            <div className="emailInputWrapper">
            <input
              type="email"
              placeholder=" "
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="emailInput"
            />
            <label className="emailLabel">Enter your email</label>
          </div>

            <button
              type="submit"
              disabled={emailLoading}
              className="signInBtnOther"
            >
              {emailLoading ? "Sending..." : "Send login link"}
            </button>

            {emailError && (
              <p style={{ color: "#8D2D2D", fontSize: "14px" }}>
                {emailError}
              </p>
            )}
          </form>
        ) : (
          <p style={{ marginTop: "16px" }}>
            Check your email — your Fika link is waiting ☕
          </p>
        )}

        <p className="signInFooter" style={{ marginTop: "18px" }}>
          New to Fika? <Link to="/apply">Apply here.</Link>
        </p>
      </main>
    </div>
  );
}